import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Spinner } from 'react-bootstrap';

interface Props {
    show: boolean;
    onClose: () => void;
    /**
     * Callback chamado quando o usuário confirma. Deve ser uma Promise:
     * o modal mantém o spinner ativo enquanto a promise não resolve, e
     * só fecha automaticamente ao final (sucesso ou erro).
     */
    onConfirmar: (params: {
        adicionarComoNovo: boolean;
        nome: string;
        descricao: string;
        naoEnviarAssinatura: boolean;
    }) => Promise<void>;
}

/**
 * Modal apresentado quando o usuário tenta gerar um contrato e a locação já tem
 * um contrato vinculado. Permite escolher entre substituir o contrato existente
 * ou adicionar como novo documento (ex: aditivo).
 *
 * State é local para não causar re-render do componente pai a cada keystroke.
 */
export default function ModalEscolhaGerarContrato({ show, onClose, onConfirmar }: Props) {
    const [modo, setModo] = useState<'substituir' | 'novo'>('substituir');
    const [nome, setNome] = useState('');
    const [descricao, setDescricao] = useState('');
    const [naoEnviarAssinatura, setNaoEnviarAssinatura] = useState(false);
    const [processando, setProcessando] = useState(false);

    // Reseta o state sempre que o modal é aberto.
    useEffect(() => {
        if (show) {
            setModo('substituir');
            setNome('');
            setDescricao('');
            setNaoEnviarAssinatura(false);
            setProcessando(false);
        }
    }, [show]);

    const podeContinuar = modo === 'substituir' || nome.trim().length > 0;

    const handleConfirmar = async () => {
        if (processando) return;
        if (!podeContinuar) return;

        setProcessando(true);
        try {
            await onConfirmar({
                adicionarComoNovo: modo === 'novo',
                nome: nome.trim(),
                descricao: descricao.trim(),
                naoEnviarAssinatura,
            });
            // O modal pai só chama onClose após sucesso completo da geração.
            // Se chegou aqui sem exceção, a geração terminou.
        } catch {
            // Erros são tratados pelo callback (toast etc); só liberamos o botão.
        } finally {
            setProcessando(false);
        }
    };

    return (
        <Modal show={show} onHide={() => { if (!processando) onClose(); }} centered backdrop="static">
            <Modal.Header style={{ backgroundColor: '#1f467e' }} className="text-white border-0">
                <Modal.Title style={{ fontSize: 16, fontWeight: 600 }}>Esta locação já tem contrato</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p style={{ fontSize: 13, color: '#374151' }}>
                    Já existe um contrato vinculado a esta locação. O que deseja fazer?
                </p>
                <Form>
                    <Form.Check
                        type="radio"
                        id="opt-substituir"
                        name="opcaoContrato"
                        label="Substituir o contrato existente"
                        checked={modo === 'substituir'}
                        onChange={() => setModo('substituir')}
                        disabled={processando}
                        className="mb-2"
                    />
                    <Form.Check
                        type="radio"
                        id="opt-novo"
                        name="opcaoContrato"
                        label="Adicionar como novo documento (ex: aditivo)"
                        checked={modo === 'novo'}
                        onChange={() => setModo('novo')}
                        disabled={processando}
                        className="mb-2"
                    />
                    {modo === 'novo' && (
                        <div style={{ marginTop: 12, padding: 12, background: '#f8f9fb', borderRadius: 6 }}>
                            <Form.Group className="mb-2">
                                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Nome do documento *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={nome}
                                    onChange={(e) => setNome(e.target.value)}
                                    maxLength={200}
                                    placeholder="Ex: Aditivo de prorrogação"
                                    disabled={processando}
                                    autoFocus
                                />
                            </Form.Group>
                            <Form.Group>
                                <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Descrição (opcional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={descricao}
                                    onChange={(e) => setDescricao(e.target.value)}
                                    maxLength={500}
                                    disabled={processando}
                                />
                            </Form.Group>
                            <small style={{ color: '#92400e' }}>
                                Adicionar como novo ocupa um dos 3 slots de documentos extras. O contrato original permanece intacto.
                            </small>
                        </div>
                    )}
                    <hr style={{ margin: '14px 0 10px' }} />
                    <Form.Check
                        type="checkbox"
                        id="opt-nao-enviar-assinatura"
                        label="Não enviar para assinatura digital"
                        checked={naoEnviarAssinatura}
                        onChange={(e) => setNaoEnviarAssinatura(e.target.checked)}
                        disabled={processando}
                    />
                    <small style={{ color: '#6b7280', display: 'block', marginTop: 4, marginLeft: 24 }}>
                        Ao marcar, o contrato é apenas gerado e não é enviado para o Autentique.
                    </small>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose} disabled={processando}>
                    Cancelar
                </Button>
                <Button
                    style={{ backgroundColor: '#1f467e', borderColor: '#1f467e', fontWeight: 600 }}
                    disabled={!podeContinuar || processando}
                    onClick={handleConfirmar}
                >
                    {processando
                        ? <><Spinner animation="border" size="sm" className="me-2" />Gerando contrato...</>
                        : 'Continuar'
                    }
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
